import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTag, addTag, updateTag, clearCurrentTag } from '../actions/tagActions';
import TagForm from '../components/content-management/TagForm';
import ContentManagementSidebar from '../components/content-management/ContentManagementSidebar';
import { X } from 'lucide-react';
import { isAdminRole } from '../utils/roleHelpers';

const TagEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentTag, tagLoading } = useSelector(state => state.tag);
  const { user } = useSelector(state => state.auth);

  // Determine mode based on URL
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id;

  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    const loadData = async () => {
      try {
        // If we have an ID, load the tag
        if (id) {
          await dispatch(getTag(id));
        }
      } catch (error) {
        console.error('Error loading tag:', error);
      }
    };

    loadData();

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentTag());
    };
  }, [dispatch, id]);

  const handleSubmit = async (tagData) => {
    try {
      const action = id ? updateTag(id, tagData) : addTag(tagData);
      await dispatch(action);
      // Redirect back to tags list
      navigate('/content-management/tags');
    } catch (error) {
      console.error('Error saving tag:', error);
    }
  };

  const handleClose = () => {
    navigate('/content-management/tags');
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Tags') {
      navigate('/content-management/tags');
    } else {
      navigate('/content-management', { state: { activeTab: tabId } });
    }
  };

  // Show loading state
  if (tagLoading && id) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Tags"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={false}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="h-8 w-48 bg-gray-200 rounded animate-shimmer"></div>
                <button
                  type="button"
                  onClick={() => navigate('/content-management/tags')}
                  className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Form Skeleton */}
              <div className="space-y-6">
                <div>
                  <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-shimmer"></div>
                  <div className="h-10 bg-gray-200 rounded animate-shimmer"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If trying to edit/view a tag that doesn't exist
  if (id && !currentTag && !tagLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Tags"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={false}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-red-500 text-xl mb-4">Tag not found</div>
              <button
                onClick={() => navigate('/content-management/tags')}
                className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
              >
                Return to Tags
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mode = isViewMode ? 'view' : isEditMode ? 'edit' : 'new';

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Tags"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={false}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
          <TagForm
            mode={mode}
            tag={currentTag}
            onSubmit={handleSubmit}
            onClose={handleClose}
          />
        </div>
      </div>
    </div>
  );
};

export default TagEditor;
